'use client';
import React, {
  ForwardedRef,
  forwardRef,
  ReactNode,
  RefObject,
  MouseEvent,
  useMemo,
  FC,
  isValidElement,
  ReactElement,
} from 'react';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import clsx from 'clsx';
import isNil from 'lodash/isNil';
import Link from 'next/link';
import LinkIcon from '@/core/icons/link';
import ToolTip from '@/core/components/Tooltip';
import {
  notEmptyString,
} from '@/utils/misc';
import {
  FormattedNamesDTO,
} from '@/dtos';
import {
  getDisplayName,
  getInitials,
} from '@/utils/names';
import {
  NameFormatKey,
} from '@/types/nameFormat';
import {
  getUserInitials,
} from '@/utils/string';

type CommonComponentType = FC<{
  className?: string;
}>;

type LinkProps = {
  href?: string;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  Icon?: CommonComponentType;
};

type Part = 'root' | 'label' | 'nameRoot' | 'link' | 'icon';

type AvatarProps = {
  variant?: 'circular' | 'rounded' | 'square';
  src?: string;
  alt?: string;
  className?: string;
  bgColor?: string;
};

type Props = Pick<FormattedNamesDTO, NameFormatKey> & {
  className?: string;
  labelClassName?: string;
  name?: ReactNode | string;
  placeholder?: string;
  children?: ReactNode;
  image?: ReactElement | AvatarProps | boolean;
  link?: LinkProps | string;
  classes?: Partial<Record<Part, string>>;
  error?: string;
};

const containerClasses = 'flex gap-2 items-center w-full group/name';

function Name({
  className,
  labelClassName,
  classes,
  name,
  image,
  link,
  error,
  children,
  placeholder = '',
  ...props
}: Props, ref: ForwardedRef<HTMLAnchorElement | HTMLDivElement>) {
  const displayName = getDisplayName(props, placeholder);
  const resolvedDisplayName = typeof name === 'string'
    ? displayName || name || ''
    : displayName ?? '';

  const initials = getInitials(props);

  const isCustomLink = typeof link !== 'string';
  const href = isCustomLink ? link?.href ?? null : link;

  const hasImage = !isNil(image);
  const hasError = notEmptyString(error);

  const avatarProps: AvatarProps = typeof image === 'object' && !isValidElement(image)
    ? image
    : {};

  const avatarInitials = initials ?? getUserInitials(resolvedDisplayName) ?? '';

  const icon = isValidElement(image)
    ? image
    : (
      <Avatar
        alt={avatarProps.alt ?? resolvedDisplayName}
        src={avatarProps.src}
        variant={avatarProps.variant ?? 'circular'}
        className={clsx(
          'w-8 h-8 text-[12px] font-medium',
          { 'border border-gray-200': !hasError },
          avatarProps.className,
        )}
        sx={{
          width: 32,
          height: 32,
          backgroundColor: avatarProps.bgColor ?? '#2563eb',
          fontSize: '12px',
          fontWeight: 500,
          color: '#ffffff',
        }}
      >
        {avatarInitials}
      </Avatar>
    );

  const hasChildren = !isNil(children);
  const isLink = !isNil(link);

  const rootClass = clsx(
    'relative w-full flex items-center gap-2 font-medium',
    className,
    classes?.root,
  );

  const labelClasses = clsx(
    'truncate',
    { 'group-hover/name:text-blue-500': isLink },
    labelClassName,
    classes?.label,
  );

  const IconLink = useMemo(() => (
    (isCustomLink && !isNil(link?.Icon) ? link!.Icon : LinkIcon) as CommonComponentType
  ), [link, isCustomLink]);

  const imageVariant = (avatarProps?.variant ?? 'circular');

  return (
    <div className={rootClass}>
      {hasImage && (
        <ToolTip
          title={error}
          hide={!hasError}
        >
          <Badge
            className={clsx({
              'border-4 border-red-600 cursor-pointer': hasError,
              'rounded-lg': imageVariant === 'rounded',
              'rounded-full': imageVariant === 'circular',
            })}
            invisible={!hasError}
            badgeContent="!"
            color="error"
          >
            {icon}
          </Badge>
        </ToolTip>
      )}
      <div className={clsx('flex flex-col truncate flex-1', classes?.nameRoot)}>
        {isLink
          ? (
            <Link
              ref={ref as RefObject<HTMLAnchorElement>}
              href={href ?? ''}
              className={clsx(containerClasses, classes?.link)}
              onClick={(e: MouseEvent<HTMLAnchorElement>) => {
                if (isCustomLink && typeof link?.onClick === 'function') {
                  e.preventDefault();
                  link.onClick(e);
                }
              }}
            >
              <h2 className={labelClasses}>
                {resolvedDisplayName}
              </h2>
              <IconLink
                className={clsx(
                  '!text-sm group-hover/name:!text-blue-500',
                  { '!hidden group-hover/name:!block': isNil(classes?.icon) },
                  classes?.icon,
                )}
              />
            </Link>
          )
          : (
            <div
              ref={ref as RefObject<HTMLDivElement>}
              className={clsx(containerClasses, classes?.link)}
            >
              <h2 className={labelClasses}>
                {resolvedDisplayName}
              </h2>
            </div>
          )}
        {hasChildren && children}
      </div>
    </div>
  );
}

export default forwardRef(Name);
